import { useEffect, useRef, useState } from "react";
import type {
  FieldData,
  FiltersToPin,
} from "../../FilterManagement/CommonFilterTypes";
import type { FilterEntity } from "../../FilterManagement/filterEntity";
import ConditionsRow from "../ConditionsRow/ConditionsRow";
import ActionButtons from "../../ActionButtons";
import "./ConditionRowsPanel.scss";

/** A single panel entry from the react-hook-form field array. Only `id` is
 * read here; the rest is forwarded as form state. */
interface ControlledField {
  id: string;
  [key: string]: unknown;
}

interface ConditionRowsPanelProps {
  entity?: FilterEntity;
  controlledFields?: ControlledField[];
  previewFilter: (value?: boolean) => void;
  remove: (index: number) => void;
  onAddConditions: () => void;
  disableAddCondition?: boolean;
  disableSave?: boolean;
  disableSaveAs?: boolean;
  isDirty?: boolean;
  saveAs?: (() => void) | null;
  reset?: (() => void) | null;
  selectedId?: string;
  fieldsData?: FieldData[];
  isEditable?: boolean;
  context?: string;
  location?: string;
  onEditHandler?: () => void;
  showEditButton?: boolean;
  showSaveButtons?: boolean;
  showCancelButton?: boolean;
  showAddValue?: boolean;
  showAddCondition?: boolean;
  statusesData?: unknown[];
  shouldBlinkField?: string;
  shouldOpenField?: string;
  updateFilter?: (state: Record<string, unknown>) => void;
  isSaving?: boolean;
  showDelete?: boolean;
  allowRemoveAllConditions?: boolean;
  onLastConditionRowRemove: () => void;
  chipPopoverClassName?: string;
  filtersToPin?: FiltersToPin;
  unsupportedFilterFields?: string[];
  addUsedField?: (field: string) => void;
  removeUsedField?: (field: string) => void;
  saveTooltipContent?: string;
}

const ConditionRowsPanel: React.FC<ConditionRowsPanelProps> = ({
  entity,
  controlledFields,
  previewFilter,
  remove,
  onAddConditions,
  disableAddCondition,
  disableSave,
  disableSaveAs,
  isDirty,
  saveAs,
  reset,
  selectedId,
  fieldsData,
  isEditable,
  context,
  location,
  onEditHandler,
  showEditButton,
  showSaveButtons,
  showCancelButton,
  showAddValue,
  showAddCondition,
  statusesData,
  shouldBlinkField,
  shouldOpenField,
  updateFilter,
  isSaving,
  showDelete,
  allowRemoveAllConditions,
  onLastConditionRowRemove,
  chipPopoverClassName,
  filtersToPin,
  unsupportedFilterFields,
  addUsedField = (field: string) => {},
  removeUsedField = (field: string) => {},
  saveTooltipContent,
}) => {
  const [openPopoverRowId, setOpenPopoverRowId] = useState<string | null>(null);
  const [showConditions, setShowConditions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasMultipleRows = (controlledFields?.length ?? 0) > 1;

  const hasOpenChipPopoverInPanel = openPopoverRowId !== null;

  const toggleShowConditions = () => {
    setShowConditions(!showConditions);
  };

  const addCondition = () => {
    setShowConditions(true);
    onAddConditions();
  };

  useEffect(() => {
    setShowConditions(false);
  }, [selectedId]);

  const scrollToBottom = () => {
    containerRef?.current &&
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
  };

  useEffect(() => {
    // toggle to the bottom of the component.
    if ((controlledFields?.length ?? 0) > 3) {
      scrollToBottom();
    }
  }, [controlledFields?.length]);

  return (
    <div ref={containerRef} className={`panel-data`}>
      {controlledFields?.map((field, index) => (
        <div
          className={`condition-row-wrap ${
            showConditions || index === 0 ? "is-shown" : "is-hidden"
          } ${openPopoverRowId === field?.id || isSaving ? "is-active" : ""}`}
          key={field?.id}
        >
          {index > 0 && <div className={`or-mark`}>OR</div>}

          <div className={`condition-row`}>
            {(showConditions || index === 0) && (
              <div className={`panel-data-wrap-with-conditions`}>
                <ConditionsRow
                  entity={entity}
                  showConditions={showConditions}
                  toggleShowConditions={toggleShowConditions}
                  disableAddCondition={disableAddCondition}
                  namePrefix={`filterPanels[${index}]`}
                  key={field?.id}
                  removeRow={() => remove(index)}
                  previewFilter={previewFilter}
                  rowIndex={index}
                  onLastConditionRemove={() => {
                    const isOnlyRow =
                      controlledFields?.length === 1 && index === 0;
                    if (isOnlyRow) {
                      onLastConditionRowRemove();
                    }
                  }}
                  isOnlyRow={controlledFields?.length === 1 && index === 0}
                  onAddConditions={addCondition}
                  totalConditionsAmount={(controlledFields?.length ?? 0) - 1}
                  showAddCondition={
                    index === (controlledFields?.length ?? 0) - 1 &&
                    showAddCondition
                  }
                  onPopoverOpenChange={(isOpen) =>
                    setOpenPopoverRowId((prevRowId) =>
                      isOpen
                        ? field?.id
                        : prevRowId === field?.id
                          ? null
                          : prevRowId
                    )
                  }
                  showToggleConditionsButton={index === 0 && hasMultipleRows}
                  // Non-null assertions: ConditionRowsPanel types these props
                  // as optional, but every caller (e.g. FilterPanel) provides
                  // them. Runtime is unchanged — assertions are erased.
                  fieldsData={fieldsData!}
                  isEditable={isEditable}
                  context={context!}
                  showAddValue={showAddValue}
                  statusesData={statusesData}
                  shouldBlinkField={shouldBlinkField}
                  shouldOpenField={shouldOpenField}
                  updateFilter={updateFilter!}
                  allowRemoveAll={allowRemoveAllConditions}
                  chipPopoverClassName={chipPopoverClassName}
                  filtersToPin={filtersToPin}
                  unsupportedFilterFields={unsupportedFilterFields}
                  addUsedField={addUsedField}
                  removeUsedField={removeUsedField}
                />

                {/* saveAs - only when everything is valid */}
                {/* save - only when there`s changes on the scope filters it w`ont be disabled*/}
                <ActionButtons
                  location={location}
                  showDelete={showDelete && hasMultipleRows && !showEditButton}
                  disableDelete={hasOpenChipPopoverInPanel}
                  disableSave={disableSave || !controlledFields?.length}
                  disableSaveAs={disableSaveAs || !controlledFields?.length}
                  disableCancelEdit={!isDirty}
                  onSave={index === 0 && showSaveButtons}
                  onSaveAs={index === 0 && showSaveButtons ? saveAs : null}
                  onCancelHandler={
                    index === 0 && showCancelButton ? reset : null
                  }
                  onDelete={() => {
                    setTimeout(() => {
                      remove(index);
                    }, 0);

                    setTimeout(() => {
                      previewFilter(true);
                    }, 10);
                  }}
                  showEditButton={index === 0 && showEditButton}
                  onEditHandler={onEditHandler}
                  isSaving={isSaving}
                  saveTooltipContent={saveTooltipContent}
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ConditionRowsPanel;
